import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createClass, createLabBatches, fetchProgramMapping } from '../services/classService';
import LabBatchSetupModal from '../modals/LabBatchSetupModal';
import LabBatchConfirmationModal from '../modals/LabBatchConfirmationModal';
import {
  FiBook, FiRefreshCw, FiLayers, FiUsers, FiX, FiDatabase,
  FiServer, FiGlobe, FiCommand, FiCheckCircle, FiLock, FiClock
} from 'react-icons/fi';

// --- UTILITY: Subject Categorizer (Pure Function) ---
const getSubjectCategory = (subject) => {
  if (!subject) return 'UNKNOWN';
  
  const type = (subject.subjectType || '').toLowerCase();
  const isLab = subject.isLab || type === 'lab';
  const isElective = subject.isElective || type === 'elective';
  const isLanguage = subject.isLanguage || type === 'language';
  const requiresGroup = subject.requiresGroupManagement === true;
  

  if (isLab) return 'LAB';
  if ((isElective || isLanguage) && requiresGroup) return 'CHOICE_BASED';
  if (isElective || isLanguage) return 'COMMON_ELECTIVE';
  return 'CORE';
};

// --- UTILITY: Badge Styler ---
const getBadgeStyle = (category, subjectName = '') => {
  switch (category) {
    case 'LAB':
      return { 
        bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', 
        icon: FiServer, label: 'Laboratory' 
      };
    case 'CHOICE_BASED':
      return { 
        bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', 
        icon: FiUsers, label: 'Choice-Based' 
      };
    case 'COMMON_ELECTIVE':
      return { 
        bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', 
        icon: FiGlobe, label: 'Common Subject' 
      };
    default:
      return { 
        bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', 
        icon: FiBook, label: 'Core' 
      };
  }
};

// --- Display Component for Read-only Fields ---
const ReadOnlyField = ({ label, value, icon: Icon, isLoading = false }) => (
  <div className="group">
    <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 block tracking-wide flex items-center gap-1">
      <FiLock className="w-3 h-3" />
      {label}
    </label>
    <div className={`
      w-full bg-slate-50 border rounded-md py-2.5 pl-3 pr-10 text-sm font-medium
      ${isLoading ? 'animate-pulse bg-slate-100' : 'text-slate-700 border-slate-300'}
    `}>
      {isLoading ? (
        <div className="flex items-center gap-2">
          <FiRefreshCw className="animate-spin w-3 h-3 text-slate-400" />
          <span className="text-slate-400">Loading...</span>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <span className="font-medium text-slate-800">{value || '—'}</span>
          {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        </div>
      )}
    </div>
  </div>
);

const CreateClassForm = ({
  tenantId, userId, selectedAcademicYear, semesterId, termId, isSemesterBased,
  academicYears = [], semesters = [], terms = [], programs = [], programTypes = [],
  closeModal, onClassCreated, notify, isEditMode, classToEdit,
  mappingData = null, programId: propProgramId = '', programTypeId: propProgramTypeId = '',
  programName: propProgramName = '', programTypeName: propProgramTypeName = '',
  academicYearName: propAcademicYearName = '', semesterName: propSemesterName = '',
  isLoading = false // Add loading prop
}) => {
  
  // ------------------ STATE ------------------
  const [localProgramId, setLocalProgramId] = useState(propProgramId || '');
  const [localProgramTypeId, setLocalProgramTypeId] = useState(propProgramTypeId || '');
  const [localAcademicYearId, setLocalAcademicYearId] = useState(selectedAcademicYear || '');
  const [localSemesterId, setLocalSemesterId] = useState(semesterId || '');
  const [localTermId, setLocalTermId] = useState(termId || '');
  const [selectedTermObj, setSelectedTermObj] = useState(null);
  const [className, setClassName] = useState('');
  const [maxStudents, setMaxStudents] = useState(30);
  
  const [submitting, setSubmitting] = useState(false);
  const [loadingMapping, setLoadingMapping] = useState(false);
  const [mappingInfo, setMappingInfo] = useState(mappingData || null);
  const [, setUseMapping] = useState(!!mappingData);

  // Lab Flow State
  const [showLabBatchConfirmation, setShowLabBatchConfirmation] = useState(false);
  const [showLabBatchSetup, setShowLabBatchSetup] = useState(false);
  const [, setLabConfig] = useState(null);
  const [isCreatingWithLabs, setIsCreatingWithLabs] = useState(false);
  
  // Refs
  const classPayloadRef = useRef(null);
  const prevContextRef = useRef({});

  // ------------------ DEBUG LOGS ------------------
  useEffect(() => {
    console.log('[CreateClassForm] terms prop:', terms);
    console.log('[CreateClassForm] semesters prop:', semesters);
    console.log('[CreateClassForm] termId prop:', termId);
    console.log('[CreateClassForm] semesterId prop:', semesterId);
    console.log('[CreateClassForm] isLoading prop:', isLoading);
  }, [terms, semesters, termId, semesterId, isLoading]);

  // ------------------ SYNC WITH PROPS ------------------
  useEffect(() => {
    if (propProgramId) setLocalProgramId(propProgramId);
    if (propProgramTypeId) setLocalProgramTypeId(propProgramTypeId);
    if (selectedAcademicYear) setLocalAcademicYearId(selectedAcademicYear);
    if (semesterId) setLocalSemesterId(semesterId);
    if (termId) setLocalTermId(termId);
  }, [propProgramId, propProgramTypeId, selectedAcademicYear, semesterId, termId]);

  // Update selectedTermObj when termId or terms change
  useEffect(() => {
    if (termId && terms && terms.length > 0) {
      const foundTerm = terms.find(t => 
        String(t._id) === String(termId) || String(t.id) === String(termId)
      );
      console.log('[CreateClassForm] Setting selectedTermObj from termId:', termId, 'found:', foundTerm);
      setSelectedTermObj(foundTerm || null);
    }
  }, [termId, terms]);

  // Handler for term selection
  const handleTermChange = (termId) => {
    setLocalTermId(termId);
    const foundTerm = terms.find(t => String(t._id) === String(termId) || String(t.id) === String(termId));
    setSelectedTermObj(foundTerm || null);
  };

  // ------------------ DERIVED VALUES ------------------
  
  // Check if context is locked (provided from parent)
  const isContextLocked = useMemo(() => 
    propProgramId && propProgramTypeId && selectedAcademicYear,
    [propProgramId, propProgramTypeId, selectedAcademicYear]
  );

  // Find display names for read-only fields
  const selectedProgramName = useMemo(() => 
    propProgramName || programs.find(p => p._id === localProgramId)?.name || localProgramId,
    [programs, localProgramId, propProgramName]
  );

  const selectedProgramTypeName = useMemo(() => 
    propProgramTypeName || programTypes.find(pt => pt._id === localProgramTypeId)?.name || localProgramTypeId,
    [programTypes, localProgramTypeId, propProgramTypeName]
  );

  const selectedAcademicYearName = useMemo(() => {
    const ay = academicYears.find(ay => ay._id === localAcademicYearId);
    return ay?.name || ay?.yearName || localAcademicYearId || '—';
  }, [academicYears, localAcademicYearId]);

  const selectedSemesterName = useMemo(() => {
    if (!isSemesterBased) return null;
    
    // Show loading state
    if (isLoading) {
      return 'Loading...';
    }
    
    // Safety check for empty array
    if (!semesters || semesters.length === 0) {
      console.log('[selectedSemesterName] semesters array is empty, using localSemesterId:', localSemesterId);
      return localSemesterId || '—';
    }
    
    const foundSemester = semesters.find(s => String(s._id) === String(localSemesterId));
    console.log('[selectedSemesterName] localSemesterId:', localSemesterId, 'foundSemester:', foundSemester);
    
    return foundSemester?.name || 
           foundSemester?.semesterName || 
           (foundSemester?.semesterNumber ? `Semester ${foundSemester.semesterNumber}` : null) || 
           localSemesterId || 
           '—';
  }, [semesters, localSemesterId, isSemesterBased, isLoading]);

  const selectedTermName = useMemo(() => {
    if (isSemesterBased) return null;
    
    // Show loading state
    if (isLoading) {
      return 'Loading...';
    }
    
    // First check selectedTermObj
    if (selectedTermObj) {
      console.log('[selectedTermName] selectedTermObj:', selectedTermObj, 'localTermId:', localTermId);
      return selectedTermObj.name || 
             selectedTermObj.termLabel || 
             (selectedTermObj.termNumber ? `Term ${selectedTermObj.termNumber}` : null) || 
             localTermId || 
             '—';
    }
    
    // Safety check for empty array
    if (!terms || terms.length === 0) {
      console.log('[selectedTermName] terms array is empty, using localTermId:', localTermId);
      return localTermId || '—';
    }
    
    console.log('[selectedTermName] terms:', terms, 'localTermId:', localTermId);
    const foundTerm = terms.find(t => {
      const termId = typeof t._id === 'object' && t._id.$oid ? t._id.$oid : t._id || t.id;
      return String(termId) === String(localTermId);
    });
    console.log('[selectedTermName] foundTerm:', foundTerm, 'localTermId:', localTermId);
    
    return foundTerm?.name || 
           foundTerm?.termLabel || 
           (foundTerm?.termNumber ? `Term ${foundTerm.termNumber}` : null) || 
           localTermId || 
           '—';
  }, [selectedTermObj, terms, localTermId, isSemesterBased, isLoading]);

  // ------------------ MEMOIZED CALCULATIONS ------------------
  
  const processedMapping = useMemo(() => {
    if (!mappingInfo?.departmentSubjects) return { 
      departments: [], 
      allSubjects: [], 
      labSubjects: [],
      stats: { total: 0, labs: 0, credits: 0 } 
    };

    const departments = [];
    const allSubjects = [];
    let totalCredits = 0;

    mappingInfo.departmentSubjects.forEach(dept => {
      const deptId = dept.departmentId?._id || dept.departmentId;
      const deptName = dept.departmentId?.name || 'Unknown Dept';
      
      const processedSubjects = (dept.subjects || []).map(s => {
        const subj = s.subjectId;
        if (!subj) return null;
        
        const category = getSubjectCategory(subj);
        const style = getBadgeStyle(category);
        
        if (subj.isCreditBased) totalCredits += (subj.credits || 0);

        const cleanSubject = {
          _id: subj._id,
          name: subj.name,
          code: subj.subjectCode,
          credits: subj.credits,
          category,
          style,
          original: subj
        };

        allSubjects.push(cleanSubject);
        return cleanSubject;
      }).filter(Boolean);

      const sortOrder = { 'CORE': 1, 'COMMON_ELECTIVE': 2, 'CHOICE_BASED': 3, 'LAB': 4 };
      processedSubjects.sort((a, b) => sortOrder[a.category] - sortOrder[b.category]);

      departments.push({
        id: deptId,
        name: deptName,
        subjects: processedSubjects
      });
    });

    const labSubjects = allSubjects.filter(s => s.category === 'LAB');

    return {
      departments,
      allSubjects,
      labSubjects,
      stats: {
        total: allSubjects.length,
        labs: labSubjects.length,
        credits: totalCredits
      }
    };
  }, [mappingInfo]);

  // Filtered programs
  const filteredPrograms = useMemo(() => 
    programs.filter(p => (p.programTypeId?._id || p.programTypeId) === localProgramTypeId),
  [programs, localProgramTypeId]);

  // ------------------ MAPPING FETCH ------------------

  const fetchMapping = useCallback(async () => {
    if (!localProgramId || !localProgramTypeId || !localAcademicYearId) {
      setMappingInfo(null); 
      return;
    }
    
    // Validate based on system type
    if (isSemesterBased && !localSemesterId) {
      setMappingInfo(null); 
      return;
    }
    if (!isSemesterBased && !localTermId) {
      setMappingInfo(null); 
      return;
    }

    setLoadingMapping(true);
    try {
      const params = { 
        tenantId, 
        programId: localProgramId, 
        programTypeId: localProgramTypeId, 
        academicYearId: localAcademicYearId
      };
      
      if (isSemesterBased && localSemesterId) {
        params.semesterId = localSemesterId;
      } else if (!isSemesterBased && localTermId) {
        params.termId = localTermId;
      }
      
      const res = await fetchProgramMapping(params);
      if (res?.mapping) {
        setMappingInfo(res.mapping);
        setUseMapping(true);
      } else {
        setMappingInfo(null);
        setUseMapping(false);
      }
    } catch (err) {
      console.error('Error fetching mapping:', err);
      setMappingInfo(null);
    } finally {
      setLoadingMapping(false);
    }
  }, [
    tenantId,
    localProgramId,
    localProgramTypeId,
    localAcademicYearId,
    localSemesterId,
    localTermId,
    isSemesterBased
  ]);

  // Trigger Fetch on Context Change
  useEffect(() => {
    if (mappingData) return; // Skip if provided from parent

    const ctx = { 
      p: localProgramId, 
      pt: localProgramTypeId, 
      ay: localAcademicYearId, 
      s: localSemesterId,
      t: localTermId,
      isSem: isSemesterBased
    };
    const prev = prevContextRef.current;
    
    const changed = Object.keys(ctx).some(k => ctx[k] !== prev[k]);
    if (changed) {
      prevContextRef.current = ctx;
      const timer = setTimeout(fetchMapping, 50);
      return () => clearTimeout(timer);
    }
  }, [fetchMapping, mappingData, localProgramId, localProgramTypeId, localAcademicYearId, 
      localSemesterId, localTermId, isSemesterBased]);

  // ------------------ SUBMISSION HANDLERS ------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!className.trim()) {
      notify('Please enter a class name', 'error');
      return;
    }
    
    if (!mappingInfo) {
      notify('Curriculum mapping data not loaded', 'error');
      return;
    }
    
    if (!maxStudents || maxStudents < 1) {
      notify('Maximum students is required and must be a positive number', 'error');
      return;
    }

    // Prepare payload with proper context fields
    const deptIds = processedMapping.departments.map(d => d.id);
    const organizationId = academicYears.find(y => (y._id || y.id) === localAcademicYearId)?.organizationId;

    const payload = {
      tenantId,
      organizationId,
      academicYearId: localAcademicYearId,
      programId: localProgramId,
      programTypeId: localProgramTypeId,
      departmentIds: deptIds,
      classes: [{
        className: className.trim(),
        departmentSubjects: processedMapping.departments.map(d => ({
          departmentId: d.id,
          subjectIds: d.subjects.map(s => s._id)
        })),
        maxStudents
      }],
      createdBy: userId,
      useMappingSubjects: true,
      programSubjectMappingId: mappingInfo._id
    };

    // Add semesterId OR termId based on academic year type
    if (isSemesterBased) {
      payload.semesterId = localSemesterId;
      payload.classes[0].semesterId = localSemesterId;
    } else {
      payload.termId = localTermId;
      payload.classes[0].termId = localTermId;
    }

    classPayloadRef.current = payload;

    // Check if lab subjects exist
    if (processedMapping.stats.labs > 0) {
      setShowLabBatchConfirmation(true);
    } else {
      setSubmitting(true);
      setIsCreatingWithLabs(false);
      await createClassFinal(payload, null);
    }
  };

  const handleCreateNow = () => {
    setShowLabBatchConfirmation(false);
    setShowLabBatchSetup(true);
  };

  const handleCreateLater = () => {
    setShowLabBatchConfirmation(false);
    setSubmitting(true);
    setIsCreatingWithLabs(false);
    createClassFinal(classPayloadRef.current, null);
  };

  const handleLabSetupComplete = async (success, config) => {
    setShowLabBatchSetup(false);
    
    if (success && config) {
      setLabConfig(config);
      setSubmitting(true);
      setIsCreatingWithLabs(true);
      await createClassFinal(classPayloadRef.current, config);
    } else {
      setSubmitting(false);
    }
  };

  const createClassFinal = async (payload, labConfig) => {
    try {
      // Create class
      const res = await createClass(payload);
      const createdClass = res.createdClass || res;
      
      // If lab config exists, create lab batches
      if (labConfig && processedMapping.stats.labs > 0) {
        try {
          await createLabBatches({
            classId: createdClass._id,
            batchCount: labConfig.batchCount,
            tenantId,
            organizationId: payload.organizationId,
            createdBy: userId,
            defaultCapacity: labConfig.defaultCapacity,
            batchPrefix: labConfig.batchPrefix || 'Lab Batch',
            labSubjectIds: processedMapping.labSubjects.map(s => s._id)
          });
          
          notify(`Class created with ${labConfig.batchCount} lab batches!`, 'success');
          onClassCreated && onClassCreated(createdClass, true, true);
        } catch (labError) {
          console.error('Lab batch creation failed:', labError);
          notify('Class created, but lab batch setup failed. You can set up labs later.', 'warning');
          onClassCreated && onClassCreated(createdClass, false, true);
        }
      } else {
        notify('Class created successfully!', 'success');
        onClassCreated && onClassCreated(createdClass, false, true);
      }
      
      closeModal();
    } catch (err) {
      console.error('Class creation failed:', err);
      setSubmitting(false);
      setIsCreatingWithLabs(false);
      notify(err.message || 'Creation failed', 'error');
    }
  };

  // ------------------ RENDER ------------------

  const getButtonText = () => {
    if (submitting) {
      return isCreatingWithLabs ? 'Creating Class with Labs...' : 'Creating Class...';
    }
    return isEditMode ? 'Update Class' : 'Create Class';
  };

  const getFooterMessage = () => {
    if (submitting) {
      return isCreatingWithLabs ? 'Creating class with lab batches...' : 'Creating class...';
    }
    if (isLoading) return 'Loading academic data...';
    if (!mappingInfo) return 'Please wait for curriculum data to load';
    if (!className.trim()) return 'Please enter a class name';
    if (!maxStudents || maxStudents < 1) return 'Please enter valid maximum students';
    return 'Ready to create class';
  };

  // Custom Select Component
  const CustomSelect = ({ label, value, onChange, options, disabled, loading, placeholder }) => (
    <div className="relative group">
      <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 block tracking-wide">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          disabled={disabled || loading}
          className={`
            w-full appearance-none bg-slate-50 border rounded-md py-2.5 pl-3 pr-10 text-sm font-medium transition-all
            ${disabled ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'text-slate-700 border-slate-300 hover:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500'}
          `}
        >
          <option value="">{loading ? 'Loading...' : placeholder}</option>
          {options.map(opt => (
            <option key={opt.id || opt._id} value={opt.id || opt._id}>
              {opt.name || opt.termLabel || opt.semesterName || `Term ${opt.termNumber}` || `Semester ${opt.semesterNumber}`}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {loading ? (
            <FiRefreshCw className="animate-spin text-indigo-500 w-4 h-4" />
          ) : (
            <svg className={`w-4 h-4 ${disabled ? 'text-slate-300' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 font-sans antialiased">
        <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full h-[90vh] flex flex-col overflow-hidden border border-slate-200">
          
          {/* HEADER */}
          <div className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center shrink-0">
            <div>
              <h2 className="text-lg font-serif font-bold tracking-wide flex items-center gap-2">
                <FiLayers className="text-indigo-400" />
                {isEditMode ? 'Modify Class' : 'Create Class'}
                {isContextLocked && <span className="text-xs bg-slate-700 px-2 py-1 rounded ml-2">Context Locked</span>}
              </h2>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">
                Academic ERP &bull; {isSemesterBased ? 'Semester System' : 'Term System'}
              </div>
            </div>
            <button 
              onClick={closeModal} 
              disabled={submitting}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-white disabled:opacity-50"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* MAIN BODY */}
          <div className="flex-1 flex overflow-hidden">
            
            {/* LEFT PANEL: CONFIGURATION */}
            <div className="w-full lg:w-[360px] bg-slate-50 border-r border-slate-200 flex flex-col shrink-0">
              <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6">
                
                {/* 1. Academic Context */}
                <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                    <FiCommand className="text-indigo-600" />
                    <h3 className="text-xs font-bold text-slate-800 uppercase">Context</h3>
                    {isContextLocked && (
                      <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded ml-auto">
                        Fixed by Parent
                      </span>
                    )}
                  </div>
                  
                  {isContextLocked ? (
                    // READ-ONLY DISPLAY
                    <>
                      <ReadOnlyField 
                        label="Program Type" 
                        value={selectedProgramTypeName}
                        icon={FiLock}
                        isLoading={isLoading}
                      />
                      <ReadOnlyField 
                        label="Program" 
                        value={selectedProgramName}
                        icon={FiLock}
                        isLoading={isLoading}
                      />
                      <ReadOnlyField 
                        label="Academic Year"
                        value={selectedAcademicYearName}
                        icon={FiLock}
                        isLoading={isLoading}
                      />
                      {isSemesterBased ? (
                        <ReadOnlyField 
                          label="Semester"
                          value={selectedSemesterName}
                          icon={FiLock}
                          isLoading={isLoading}
                        />
                      ) : (
                        <ReadOnlyField 
                          label="Term"
                          value={selectedTermName}
                          icon={FiClock}
                          isLoading={isLoading}
                        />
                      )}
                    </>
                  ) : (
                    // EDITABLE DROPDOWNS
                    <>
                      <CustomSelect 
                        label="Program Type"
                        value={localProgramTypeId}
                        onChange={e => { setLocalProgramTypeId(e.target.value); setLocalProgramId(''); }}
                        options={programTypes}
                        placeholder="Select Type..."
                        loading={isLoading}
                      />

                      <CustomSelect 
                        label="Program"
                        value={localProgramId}
                        onChange={e => { setLocalProgramId(e.target.value); setLocalAcademicYearId(''); }}
                        options={filteredPrograms}
                        disabled={!localProgramTypeId}
                        placeholder="Select Program..."
                        loading={isLoading}
                      />

                      <CustomSelect 
                        label="Academic Year"
                        value={localAcademicYearId}
                        onChange={e => setLocalAcademicYearId(e.target.value)}
                        options={academicYears}
                        disabled={!localProgramId}
                        placeholder="Select Year..."
                        loading={isLoading}
                      />

                      {isSemesterBased ? (
                        <CustomSelect 
                          label="Semester"
                          value={localSemesterId}
                          onChange={e => setLocalSemesterId(e.target.value)}
                          options={semesters}
                          disabled={!localAcademicYearId}
                          placeholder="Select Semester..."
                          loading={isLoading}
                        />
                      ) : (
                        <CustomSelect 
                          label="Term"
                          value={localTermId}
                          onChange={e => handleTermChange(e.target.value)}
                          options={terms}
                          disabled={!localAcademicYearId}
                          placeholder="Select Term..."
                          loading={isLoading}
                        />
                      )}
                    </>
                  )}
                </div>

                {/* 2. Class Definition */}
                <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                    <FiUsers className="text-emerald-600" />
                    <h3 className="text-xs font-bold text-slate-800 uppercase">Details</h3>
                  </div>
                  
                  <div className="group">
                    <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 block tracking-wide">
                      Section / Class Name
                    </label>
                    <input 
                      type="text"
                      value={className}
                      onChange={e => setClassName(e.target.value)}
                      placeholder="e.g. CSE-A"
                      className="w-full bg-slate-50 border border-slate-300 rounded-md py-2.5 px-3 text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all placeholder:text-slate-300"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="group mt-3">
                    <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 block tracking-wide">
                      Maximum Students <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={maxStudents}
                      onChange={e => setMaxStudents(Number(e.target.value))}
                      placeholder="e.g. 60"
                      className="w-full bg-slate-50 border border-slate-300 rounded-md py-2.5 px-3 text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all placeholder:text-slate-300"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Status Card */}
                {loadingMapping ? (
                   <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-lg flex items-center gap-3 animate-pulse">
                     <FiRefreshCw className="animate-spin text-indigo-500"/>
                     <span className="text-xs font-medium text-indigo-700">Syncing Curriculum...</span>
                   </div>
                ) : !mappingInfo ? (
                   <div className="bg-slate-100 border border-slate-200 p-4 rounded-lg text-center">
                     <p className="text-xs text-slate-500">Select context to load structure.</p>
                   </div>
                ) : (
                   <div className={`p-4 rounded-lg border flex items-start gap-3 ${
                     processedMapping.stats.labs > 0
                       ? 'bg-amber-50 border-amber-200'
                       : 'bg-emerald-50 border-emerald-100'
                   }`}>
                     <FiCheckCircle className={`
                       mt-0.5 shrink-0 ${processedMapping.stats.labs > 0 ? 'text-amber-600' : 'text-emerald-600'}
                     `}/>
                     <div>
                       <p className={`text-xs font-bold uppercase mb-0.5 ${
                         processedMapping.stats.labs > 0 ? 'text-amber-800' : 'text-emerald-800'
                       }`}>
                         {processedMapping.stats.labs > 0
                           ? `${processedMapping.stats.labs} Lab Subject${processedMapping.stats.labs !== 1 ? 's' : ''}`
                           : 'Structure Ready'}
                       </p>
                       <p className={`text-[11px] leading-tight ${
                         processedMapping.stats.labs > 0 ? 'text-amber-600' : 'text-emerald-600'
                       }`}>
                         {processedMapping.stats.labs > 0
                           ? 'Click "Create Class" to configure lab batches'
                           : 'Ready to create class'}
                       </p>
                     </div>
                   </div>
                )}
              </div>
            </div>

            {/* RIGHT PANEL: VISUALIZATION */}
            <div className="flex-1 bg-white flex flex-col overflow-hidden relative">
              
              {/* Toolbar / Stats Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white z-10 shadow-[0_2px_10px_-10px_rgba(0,0,0,0.1)]">
                <div>
                  <h3 className="text-base font-bold text-slate-800 font-serif">Curriculum Structure</h3>
                  <p className="text-slate-400 text-xs mt-0.5">Preview of subjects to be assigned</p>
                </div>
                
                {/* Micro Stats */}
                {mappingInfo && (
                  <div className="flex gap-4">
                     <div className="text-right">
                       <span className="block text-xl font-bold text-slate-800 leading-none">{processedMapping.stats.total}</span>
                       <span className="text-[10px] font-bold text-slate-400 uppercase">Subjects</span>
                     </div>
                     <div className="w-px bg-slate-200"></div>
                     <div className="text-right">
                       <span className={`block text-xl font-bold leading-none ${
                         processedMapping.stats.labs > 0 ? 'text-amber-600' : 'text-slate-800'
                       }`}>
                         {processedMapping.stats.labs}
                       </span>
                       <span className="text-[10px] font-bold text-slate-400 uppercase">Labs</span>
                     </div>
                     {maxStudents && (
                       <>
                         <div className="w-px bg-slate-200"></div>
                         <div className="text-right">
                           <span className="block text-xl font-bold text-slate-800 leading-none">{maxStudents}</span>
                           <span className="text-[10px] font-bold text-slate-400 uppercase">Capacity</span>
                         </div>
                       </>
                     )}
                  </div>
                )}
              </div>

              {/* Scrollable List */}
              <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50/30 p-6">
                {mappingInfo ? (
                  <div className="space-y-6">
                    {processedMapping.departments.map((dept) => (
                      <div key={dept.id} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                        
                        {/* Department Header */}
                        <div className="bg-slate-50/80 px-4 py-2.5 border-b border-slate-100 flex items-center justify-between sticky top-0 z-10 backdrop-blur-sm">
                          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{dept.name}</span>
                          <span className="text-[10px] font-medium bg-white border border-slate-200 px-2 py-0.5 rounded-full text-slate-500">
                            {dept.subjects.length} Subjects
                          </span>
                        </div>

                        {/* Subject Rows */}
                        <div className="divide-y divide-slate-50">
                          {dept.subjects.map((subj) => {
                            const { style } = subj;
                            const Icon = style.icon;

                            return (
                              <div key={subj._id} className="px-4 py-3 hover:bg-slate-50 transition-colors group flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3.5">
                                  {/* Icon Box */}
                                  <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${style.bg} ${style.text} border ${style.border}`}>
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  
                                  {/* Text Info */}
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-semibold text-slate-800">{subj.name}</span>
                                      
                                      {/* THE CATEGORY BADGE */}
                                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border tracking-wide ${style.bg} ${style.text} ${style.border}`}>
                                        {style.label}
                                      </span>
                                    </div>
                                    
                                    <div className="flex items-center gap-3 mt-1.5">
                                      <span className="flex items-center gap-1.5 text-xs text-slate-500 font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                                        {subj.code || 'N/A'}
                                      </span>
                                      {subj.credits > 0 && (
                                        <span className="text-[11px] font-medium text-slate-400">
                                          {subj.credits.toFixed(1)} Credits
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Right Side Logic Indicator (Group Mgmt) */}
                                {(subj.category === 'CHOICE_BASED' || subj.category === 'COMMON_ELECTIVE') && (
                                  <div className="shrink-0 mt-1">
                                     {subj.category === 'CHOICE_BASED' ? (
                                        <div className="flex flex-col items-end">
                                            <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                                              Groups
                                            </span>
                                        </div>
                                     ) : (
                                        <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                                          Unified
                                        </span>
                                     )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Empty State
                  <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                    <FiDatabase className="w-16 h-16 text-slate-300 mb-4" />
                    <p className="text-sm font-medium text-slate-600">No curriculum data loaded.</p>
                    <p className="text-xs text-slate-400 mt-1">Please configure the program context.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-between items-center shrink-0">
             <div className="text-xs text-slate-500 font-medium">
               {getFooterMessage()}
             </div>
             <div className="flex gap-3">
               <button 
                 onClick={closeModal} 
                 disabled={submitting}
                 className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-300 rounded shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
               >
                 Cancel
               </button>
               <button 
                 onClick={handleSubmit} 
                 disabled={submitting || !mappingInfo || !className.trim() || !maxStudents || maxStudents < 1 || isLoading}
                 className="px-6 py-2 text-sm font-semibold text-white bg-slate-900 rounded shadow-md hover:bg-slate-800 disabled:opacity-50 disabled:shadow-none flex items-center gap-2 transition-all"
               >
                 {submitting && <FiRefreshCw className="animate-spin" />}
                 {getButtonText()}
               </button>
             </div>
          </div>
        </div>
      </div>

      {/* MODALS */}
      <LabBatchConfirmationModal
        isOpen={showLabBatchConfirmation}
        onClose={() => setShowLabBatchConfirmation(false)}
        classData={{ className }}
        labSubjects={processedMapping.labSubjects}
        labSubjectCount={processedMapping.stats.labs}
        onCreateNow={handleCreateNow}
        onCreateLater={handleCreateLater}
        isMappingDriven={true}
      />

      {showLabBatchSetup && (
        <LabBatchSetupModal
          isOpen={showLabBatchSetup}
          onClose={() => setShowLabBatchSetup(false)}
          className={className}
          labSubjects={processedMapping.labSubjects}
          onLabBatchComplete={handleLabSetupComplete}
          mode="pre-creation"
          tenantId={tenantId}
          userId={userId}
          notify={notify}
        />
      )}
    </>
  );
};

export default CreateClassForm;